<template>
  <div class="staffing-requirement-form">
    <div class="container my-4">
      <!-- Header with actions -->
      <div class="header-section">
        <h1>{{ isEditing ? 'Edit Staffing Requirement' : 'Add Staffing Requirement' }}</h1>
        <div class="action-buttons">
          <button
            class="btn btn-secondary"
            @click="$router.push({ name: 'StaffingRequirementList' })"
          >
            <i class="fas fa-arrow-left"></i> Back to List
          </button>
        </div>
      </div>

      <!-- Error Alert -->
      <div v-if="error" class="alert alert-danger">
        {{ error }}
      </div>

      <!-- Loading indicator -->
      <div v-if="loading" class="text-center my-5">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Form -->
      <div v-if="!loading" class="card">
        <div class="card-body">
          <form @submit.prevent="saveRequirement">
            <div class="row mb-4">
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="department_id" class="form-label">Department *</label>
                  <select
                    id="department_id"
                    v-model="requirement.department_id"
                    class="form-select"
                    :class="{ 'is-invalid': errors.department_id }"
                    required
                    :disabled="isEditing"
                  >
                    <option value="" disabled>Select Department</option>
                    <option 
                      v-for="department in departments" 
                      :key="department.id" 
                      :value="department.id"
                    >
                      {{ department.name }}
                    </option>
                  </select>
                  <div v-if="errors.department_id" class="invalid-feedback">
                    {{ errors.department_id }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-6">
                <div class="form-group mb-3">
                  <label for="shift_id" class="form-label">Shift *</label>
                  <select
                    id="shift_id"
                    v-model="requirement.shift_id"
                    class="form-select"
                    :class="{ 'is-invalid': errors.shift_id }"
                    required
                    :disabled="isEditing"
                  >
                    <option value="" disabled>Select Shift</option>
                    <option 
                      v-for="shift in shifts" 
                      :key="shift.id" 
                      :value="shift.id"
                    >
                      {{ shift.name }} ({{ formatTime(shift.start_time) }} - {{ formatTime(shift.end_time) }})
                    </option>
                  </select>
                  <div v-if="errors.shift_id" class="invalid-feedback">
                    {{ errors.shift_id }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="row mb-4">
              <div class="col-md-4">
                <div class="form-group mb-3">
                  <label for="min_nurses_required" class="form-label">Minimum Nurses Required *</label>
                  <input
                    type="number"
                    id="min_nurses_required"
                    v-model.number="requirement.min_nurses_required"
                    class="form-control"
                    :class="{ 'is-invalid': errors.min_nurses_required }"
                    min="0"
                    required
                  />
                  <div v-if="errors.min_nurses_required" class="invalid-feedback">
                    {{ errors.min_nurses_required }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-4">
                <div class="form-group mb-3">
                  <label for="min_charge_nurses_required" class="form-label">Minimum Charge Nurses Required *</label>
                  <input
                    type="number"
                    id="min_charge_nurses_required"
                    v-model.number="requirement.min_charge_nurses_required"
                    class="form-control"
                    :class="{ 'is-invalid': errors.min_charge_nurses_required }"
                    min="0"
                    required
                  />
                  <div v-if="errors.min_charge_nurses_required" class="invalid-feedback">
                    {{ errors.min_charge_nurses_required }}
                  </div>
                </div>
              </div>
              
              <div class="col-md-4">
                <div class="form-group mb-3">
                  <label for="min_team_leads_required" class="form-label">Minimum Team Leads Required *</label>
                  <input
                    type="number"
                    id="min_team_leads_required"
                    v-model.number="requirement.min_team_leads_required"
                    class="form-control"
                    :class="{ 'is-invalid': errors.min_team_leads_required }"
                    min="0"
                    required
                  />
                  <div v-if="errors.min_team_leads_required" class="invalid-feedback">
                    {{ errors.min_team_leads_required }}
                  </div>
                </div>
              </div>
            </div>
            
            <div class="form-group mb-4">
              <label class="form-label">Required Specializations</label>
              <div class="specializations-container">
                <div 
                  v-for="specialization in specializations" 
                  :key="specialization.id" 
                  class="form-check mb-2"
                >
                  <input 
                    class="form-check-input" 
                    type="checkbox" 
                    :id="`specialization-${specialization.id}`"
                    :value="specialization.id"
                    v-model="requirement.required_specializations"
                  >
                  <label 
                    class="form-check-label" 
                    :for="`specialization-${specialization.id}`"
                  >
                    {{ specialization.name }}
                  </label>
                </div>
              </div>
              <div v-if="errors.required_specializations" class="text-danger mt-2">
                {{ errors.required_specializations }}
              </div>
            </div>

            <div class="form-group mb-4">
              <label for="notes" class="form-label">Notes</label>
              <textarea
                id="notes"
                v-model="requirement.notes"
                class="form-control"
                :class="{ 'is-invalid': errors.notes }"
                rows="3"
                placeholder="Additional information about this staffing requirement"
              ></textarea>
              <div v-if="errors.notes" class="invalid-feedback">
                {{ errors.notes }}
              </div>
            </div>

            <div class="form-check mb-4">
              <input 
                class="form-check-input" 
                type="checkbox" 
                id="is_active"
                v-model="requirement.is_active"
              >
              <label class="form-check-label" for="is_active">
                Active
              </label>
            </div>
            
            <div class="d-flex justify-content-end">
              <button
                type="button"
                class="btn btn-secondary me-2"
                @click="$router.push({ name: 'StaffingRequirementList' })"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="saving"
              >
                <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                {{ saving ? 'Saving...' : 'Save Requirement' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StaffingRequirementAdd',
  props: {
    id: {
      type: [Number, String],
      required: false
    }
  },
  data() {
    return {
      requirement: {
        department_id: '',
        shift_id: '',
        min_nurses_required: 1,
        min_charge_nurses_required: 0,
        min_team_leads_required: 0,
        required_specializations: [],
        is_active: true,
        notes: ''
      },
      departments: [],
      shifts: [],
      specializations: [],
      errors: {},
      error: null,
      loading: false,
      saving: false,
      isEditing: false
    }
  },
  methods: {
    formatTime(timeString) {
      if (!timeString) return '';
      
      try {
        const date = new Date(`2000-01-01T${timeString}`);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return timeString;
      }
    },
    
    async loadRequirement() {
      this.loading = true;
      
      try {
        const response = await this.$api.get(`/staffing-requirements/${this.id}`);
        this.requirement = response.data.data;
        this.isEditing = true;
      } catch (error) {
        console.error('Error loading requirement:', error);
        this.error = error.response?.data?.message || 'Failed to load staffing_requirements requirement';
        
        setTimeout(() => {
          this.$router.push({ name: 'StaffingRequirementList' });
        }, 3000);
      } finally {
        this.loading = false;
      }
    },
    
    async loadLookupData() {
      try {
        // Load departments
        const departmentsResponse = await this.$api.get('/departments');
        this.departments = departmentsResponse.data.data;

        // Load shifts
        const shiftsResponse = await this.$api.get('/shifts');
        this.shifts = shiftsResponse.data.data;

        // Load specializations
        const specializationsResponse = await this.$api.get('/specializations');
        this.specializations = specializationsResponse.data.data;
      } catch (error) {
        console.error('Error loading lookup data:', error);
        this.error = 'Failed to load required data';
      }
    },
    
    validateForm() {
      this.errors = {};
      let isValid = true;
      
      if (!this.requirement.department_id) {
        this.errors.department_id = 'Department is required';
        isValid = false;
      }
      
      if (!this.requirement.shift_id) {
        this.errors.shift_id = 'Shift is required';
        isValid = false;
      }
      
      if (this.requirement.min_nurses_required < 0) {
        this.errors.min_nurses_required = 'Minimum nurses required cannot be negative';
        isValid = false;
      }
      
      if (this.requirement.min_charge_nurses_required < 0) {
        this.errors.min_charge_nurses_required = 'Minimum charge nurses required cannot be negative';
        isValid = false;
      }
      
      if (this.requirement.min_team_leads_required < 0) {
        this.errors.min_team_leads_required = 'Minimum team leads required cannot be negative';
        isValid = false;
      }
      
      return isValid;
    },
    
    async saveRequirement() {
      if (!this.validateForm()) {
        return;
      }
      
      this.saving = true;
      this.error = null;
      
      try {
        if (this.isEditing) {
          await this.$api.put(`/staffing-requirements/${this.id}`, this.requirement);
          this.$toast.success('Staffing requirement updated successfully');
        } else {
          await this.$api.post('/staffing-requirements', this.requirement);
          this.$toast.success('Staffing requirement created successfully');
        }
        this.$router.push({ name: 'StaffingRequirementList' });
      } catch (error) {
        console.error('Error saving requirement:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.error = error.response?.data?.message || 'Failed to save staffing_requirements requirement';
        }
      } finally {
        this.saving = false;
      }
    }
  },
  mounted() {
    this.loadLookupData();
    
    if (this.id) {
      this.loadRequirement();
    }
  }
}
</script>

<style scoped>
.staffing-requirement-form {
  padding-bottom: 2rem;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.form-label {
  font-weight: 500;
}

.specializations-container {
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.75rem;
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .action-buttons {
    width: 100%;
  }
}
</style>
